import { SubmitToSandboxPanelView } from '../view/SubmitToSandboxPanelView';
import { templateSubmitToSandboxPanel } from '../view/template';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { SubmitToSandboxPanelWebComponent } from '..';
import { SubmitToSandboxPanelEvents, SubmitToSandboxPanelWebComponentSchema, SubmitToSandboxTaskData } from '../types';
import { ToastService } from '../../../../utils/toast';
import { translateFn } from '../../../../utils/translator.helper';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastActionButtonMode, ToastSeverity } from '../../../../ui/gzToastNotification/type';

export class SubmitToSandboxPanelController extends BaseWebComponentController {
  public component: SubmitToSandboxPanelWebComponent;
  public view: SubmitToSandboxPanelView;
  public viewConstructor: typeof SubmitToSandboxPanelView;

  constructor(component: SubmitToSandboxPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: SubmitToSandboxPanelView,
      viewTemplate: templateSubmitToSandboxPanel,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.saveSubmitToSandboxTask,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.emitCloseSubmitToSandboxWindowEvent,
      },
    ];
  }

  async getDataForView(): Promise<SubmitToSandboxPanelWebComponentSchema> {
    return {
      targetName: this.dependencies.getTargetName(),
    };
  }

  private async saveSubmitToSandboxTask(): Promise<void> {
    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;

    const hasCreatedTask = await this.createSubmitToSandboxTask();

    this.view.dom.$saveButton.showSpinner = false;
    this.view.dom.$saveButton.disabled = false;

    if (hasCreatedTask) {
      this.emitCloseSubmitToSandboxWindowEvent();
    }
  }

  private emitCloseSubmitToSandboxWindowEvent(): void {
    this.component.dispatchEvent(
      new CustomEvent(SubmitToSandboxPanelEvents.OnPanelClose, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  private async createSubmitToSandboxTask(): Promise<boolean> {
    const paths = this.view.dom.$samplesForm.getFormValue();
    const commandLines = this.view.dom.$settingsForm.getFormValue();

    const submitToSandboxTaskSettings: SubmitToSandboxTaskData = {
      targetName: this.dependencies.getTargetName(),
      taskName: this.view.dom.$taskNameFormField.value,
      specifics: {
        files: paths.map(path => ({ path })),
        execute: commandLines.map(commandLine => ({ commandLine })),
      },
    };

    if (this.component.apiClientInjectedFromNirvana) {
      // we leave request handling responsibility to the injected method
      await this.dependencies.api.clients.Tasks.createSubmitToSandboxTask(submitToSandboxTaskSettings);
      return true;
    }

    const errorToastMessage = translateFn('The task could not be created.');

    try {
      const createdTasksCount = await this.dependencies.api.clients.Tasks.createSubmitToSandboxTask(
        submitToSandboxTaskSettings
      ).toPromise();

      if (!createdTasksCount) {
        ToastService.showErrorToast(errorToastMessage);
        return false;
      }
    } catch (error) {
      ToastService.showErrorToast(errorToastMessage);
      return false;
    }

    this.showSuccessToastWithRedirectToTasksSection();
    return true;
  }

  private showSuccessToastWithRedirectToTasksSection() {
    const successMessage = translateFn('Task created successfully.');

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: successMessage,
      severity: ToastSeverity.SUCCESS,
      showInlineButton: true,
      hasAction: true,
      actionButtonText: translateFn('Go to Tasks section'),
      actionButtonMode: ToastActionButtonMode.LINK,
      actionCallback: () => {
        Nav.to(`/tasks`);
      },
    });
  }
}
